import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useAgendaRealtimeSync from './useAgendaRealtimeSync';

import crmOpportunityService from '../../domain/services/crm/crm.opportunity.service';
import loginService from '../../domain/services/login.service';

const scheduleFactKeys = [
    'statusName',
    'concludedAt',
    'hasOpenSchedule',
    'openSchedule',
    'scheduleCount',
    'hasContactResponseToday',
    'triageBriefing',
];

const pickScheduleFacts = (opportunity = {}) =>
    scheduleFactKeys
        .filter(key => key in opportunity)
        .reduce((facts, key) => ({ ...facts, [key]: opportunity[key] }), {});

const useOpportunityScheduleFacts = (opportunity = {}) => {

    const opportunityId = opportunity.id || null;
    const [facts, setFacts] = useState({ opportunityId: null, values: null });
    const isMountedRef = useRef(true);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const fetchFacts = useCallback(async () => {
        if (!opportunityId)
            return;

        const found = await crmOpportunityService.findById(opportunityId).catch(() => null);

        if (found && isMountedRef.current)
            setFacts({ opportunityId, values: pickScheduleFacts(found) });
    }, [opportunityId]);

    const { executeRefresh, queueRefresh } = useAgendaRealtimeSync({
        sellerId: opportunityId ? loginService.getUserAuthentication()?.sellerId : null,
        opportunityId,
        onRefresh: fetchFacts,
    });

    useEffect(() => {
        if (opportunityId)
            executeRefresh('open');
    }, [executeRefresh, opportunityId]);

    const liveOpportunity = useMemo(() => (
        facts.opportunityId === opportunityId && facts.values
            ? { ...opportunity, ...facts.values }
            : opportunity
    ), [opportunity, opportunityId, facts]);

    return { opportunity: liveOpportunity, refresh: queueRefresh };
};

export default useOpportunityScheduleFacts;
